import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getRecords } from "../../services/api.service";
import { toast } from "react-toastify";

const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
};

/**
 * @param {object} initialPayload  base /getRecords payload (memoised by the caller)
 * @param {number} page            zero based page index
 * @param {number} pageSize
 * @param {string} endpoint
 * @param {string[]} searchfields  columns / relation paths the search box looks into
 * @param {object} options
 *        options.filterWhere  extra whereCondition coming from the list filters
 *        options.onResetPage  called to send the list back to page 1 whenever the
 *                             search keyword or a filter changes
 */
const useFetchData = (initialPayload, page, pageSize, endpoint, searchfields, options = {}) => {
    const { filterWhere, onResetPage } = options;

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [totalCount, setTotalCount] = useState(0);

    const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

    // stable signature of everything that must send the user back to page 1
    const filterWhereKey = useMemo(
        () => JSON.stringify(filterWhere || {}),
        [filterWhere]
    );
    const queryKey = `${debouncedSearchKeyword.trim()}|${filterWhereKey}`;

    const queryKeyRef = useRef(queryKey);
    const [isResettingPage, setIsResettingPage] = useState(false);

    useEffect(() => {
        if (queryKeyRef.current === queryKey) return;
        queryKeyRef.current = queryKey;
        // searching / filtering while sitting on page 3 used to return an empty
        // table - always restart the result set from the first page
        if (page !== 0 && typeof onResetPage === "function") {
            setIsResettingPage(true);
            onResetPage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey]);

    useEffect(() => {
        if (isResettingPage && page === 0) setIsResettingPage(false);
    }, [page, isResettingPage]);

    const getData = useCallback(async () => {
        try {
            setIsLoading(true);
            const trimmedKeyword = debouncedSearchKeyword.trim();

            let payload = {
                ...initialPayload,
            };

            if (trimmedKeyword && searchfields?.length) {
                payload.search = {
                    field_name: searchfields,
                    searchKeyword: trimmedKeyword,
                };
            } else {
                // never ship a stale / empty search block to the API
                delete payload.search;
            }

            const parsedFilterWhere = JSON.parse(filterWhereKey);
            if (Object.keys(parsedFilterWhere).length) {
                payload.whereCondition = {
                    ...(initialPayload?.whereCondition || {}),
                    ...parsedFilterWhere,
                };
            }

            payload.pagination = {
                page: page + 1,
                pageSize: pageSize,
            }

            const responseData = await getRecords(payload, endpoint);
            if (responseData.code === 1) {
                setTotalCount(responseData.count);
                const sortedData = responseData.data
                  .map((element, index) => ({
                    ...element,
                    index: index + 1, 
                    // index: page * pageSize + (index + 1), // if you want increment sr.no then use it
                  }))
                  .sort((a, b) => b.index - a.index);
                setData(sortedData);
            } else {
                setTotalCount(0);
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Something went wrong please try again!")
        } finally {
            setIsLoading(false);
        }
        // searchfields is a fresh array on every render - depending on it would
        // restart the request loop, its contents are static per master anyway
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, debouncedSearchKeyword, initialPayload, endpoint, filterWhereKey]);

    useEffect(() => {
        if (isResettingPage) return; // a page reset is in flight, skip the stale request
        getData();
    }, [getData, isResettingPage]);

    return {
        data,
        isLoading,
        setSearchKeyword,
        searchKeyword,
        totalCount,
        refetch: getData,
    };
};

export default useFetchData;
